import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../shared/components/SEO';

const asset = (name: string) => `/images/home/${name}`;

const loopDelay = (value: string) => ({ '--loop-delay': value } as CSSProperties);

const freshAsset = (name: string) => `/images/home/${name}`;

const mosaicTiles = [
  { kind: 'image', className: 'tile-01 fade d1', image: freshAsset('user-drop-1.webp'), alt: 'ZFAST agriculture field with tractor' },
  { kind: 'loop', className: 'tile-02 green loop-tile loop-group-1 fade d2', icon: freshAsset('icon-01.webp'), label: ['Animal', 'Nutrition'] },
  { kind: 'image', className: 'tile-03 fade d3', image: freshAsset('zfast-agriculture-banner-2.webp'), alt: 'Healthy green plant sprout' },
  { kind: 'loop', className: 'tile-04 gold loop-tile loop-group-2 fade d4', icon: freshAsset('icon-02.webp'), label: ['Pure', 'Ingredients'] },
  { kind: 'image', className: 'tile-05 fade d5', image: freshAsset('hands-feeding.jpg'), alt: 'Hands holding fresh green animal feed' },
  { kind: 'image', className: 'tile-06 fade', image: freshAsset('user-drop-2.webp'), alt: 'Wheat crop in the field' },
  { kind: 'loop', className: 'tile-07 lime loop-tile loop-group-1 fade', icon: freshAsset('icon-03.webp'), label: ['Green', 'Growth'] },
  { kind: 'image', className: 'tile-08 fade', image: freshAsset('farmer-tablet.webp'), alt: 'Farmer checking field data on tablet' },
  { kind: 'loop', className: 'tile-09 teal loop-tile loop-group-2 fade', icon: freshAsset('icon-04.webp'), label: ['Quality', 'Assured'] },
  { kind: 'image', className: 'tile-10 fade', image: freshAsset('zfast-agriculture-banner-6.webp'), alt: 'Fresh green animal feed in hands' },
  { kind: 'loop', className: 'tile-11 red loop-tile loop-group-1 fade', icon: freshAsset('icon-05.webp'), label: ['Lab', 'Tested'] },
  { kind: 'image', className: 'tile-12 fade', image: freshAsset('zfast-agriculture-banner-7.webp'), alt: 'Modern ZFAST processing plant' },
  { kind: 'loop', className: 'tile-13 purple loop-tile loop-group-2 fade', icon: freshAsset('icon-06.webp'), label: ['Farmers', 'First'] },
  { kind: 'image', className: 'tile-14 fade', image: freshAsset('home-microscope.jpg'), alt: 'Quality research laboratory for agri products' },
  { kind: 'loop', className: 'tile-15 grey loop-tile loop-group-1 fade', icon: freshAsset('icon-07.webp'), label: ['Dealer', 'Network'] },
  { kind: 'image', className: 'tile-16 fade', image: freshAsset('zfast-agriculture-banner-9.webp'), alt: 'Milk splash representing dairy nutrition' },
  { kind: 'image', className: 'tile-17 fade', image: freshAsset('zfast-agriculture-banner-4.webp'), alt: 'Soybean and oilseed crop quality' },
  { kind: 'loop', className: 'tile-18 orange loop-tile loop-group-2 fade', icon: freshAsset('icon-08.webp'), label: ['Fast', 'Supply'] },
  { kind: 'loop', className: 'tile-19 green loop-tile loop-group-1 fade', icon: freshAsset('icon-09.webp'), label: ['Sustainable', 'Farming'] },
  { kind: 'loop', className: 'tile-20 gold loop-tile loop-group-2 fade', icon: freshAsset('icon-10.webp'), label: ['Trusted', 'Excellence'] }
] as const;




import { PurposeAccordion } from '../../shared/components/PurposeAccordion';
import { AnimatedBanner } from './components/AnimatedBanner';

export function Home() {

  return (
    <main className="zfast-exact-home" aria-label="ZFAST Agritech India homepage">
      <SEO 
        title="ZFAST Agritech India | Premium Cattle Feed, Pig Feed, & Edible Oils" 
        description="ZFAST Agritech India is a trusted agritech brand delivering balanced animal nutrition (cattle feed, pig feed) and quality edible oils with dealer-ready networks."
        path="/"
      />
      <AnimatedBanner />





      <section className="zfast-message">
        <div className="zfast-message__photo" aria-hidden="true">
          <picture>
            {/* The source element is redundant when it's the same as the img src. */}
            <img src={asset('zfast-right-side-generated.webp')} alt="" />
          </picture>
        </div>
        <div className="zfast-message__fade" aria-hidden="true" />

        <div className="zfast-message__content">
          <div className="zfast-message__text">
            <p className="zfast-eyebrow">Company Message</p>
            <h2 className="zfast-title">Built on relationships,<br />quality, and farmer<br />success.</h2>
            <div className="zfast-line" />

            <div className="zfast-copy">
              <p>At ZFAST Agritech India, we believe success is built on strong relationships with farmers, livestock owners, dealers, distributors, employees, and every customer who places trust in our products.</p>
              <p>Our mission is to deliver high-quality animal nutrition solutions that help support animal health, improve farm productivity, and strengthen profitability for livestock farmers.</p>
              <p>Healthy animals are the foundation of a successful dairy and livestock business. That is why our cattle feed and pig feed range is developed around balanced nutrition, dependable quality, practical farm use, and consistent supply support.</p>
              <p>As we continue to grow, we remain committed to quality, innovation, and customer satisfaction. Together, we are building a future where livestock thrive, farmers prosper, and businesses succeed.</p>
            </div>

            <div className="zfast-points" aria-label="ZFAST values">
              <div className="zfast-point">
                <img src={asset('strong-relationships.webp')} alt="Strong Relationships icon" />
                Strong<br />Relationships
              </div>
              <div className="zfast-point">
                <img src={asset('premium-quality.webp')} alt="Premium Quality icon" />
                Premium<br />Quality
              </div>
              <div className="zfast-point">
                <img src={asset('better-nutrition.webp')} alt="Better Nutrition icon" />
                Better Nutrition,<br />Better Productivity
              </div>
              <div className="zfast-point">
                <img src={asset('partnering-success.webp')} alt="Partnering in Your Success icon" />
                Partnering in<br />Your Success
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="zfast-words-section">
        <div className="zfast-words-wrap">

          <div className="zfast-words-image">
            <img 
              src="/images/about/zfast-founder.webp" 
              alt="Lalit Aggarwal, Founder and CEO of ZFAST Agritech India"
            />
            <div className="zfast-founder-caption">
              <p className="zfast-founder-name">Lalit Aggarwal</p>
              <p className="zfast-founder-title">Founder & CEO</p>
            </div>
          </div>

          <div className="zfast-words-content">
            <p className="zfast-founder-kicker" style={{ color: '#0f6b3a' }}>In His Words</p>

            <p className="zfast-quote">
              “At Zfast, we work every day to build a business rooted in strong values:
              Farmers First, Quality Consciousness, and a Happy Place to Work. Better feed
              leads to better milk, and better milk means higher income for the farmer.”
            </p>

            <p>
              ZFAST Agritech India was built with a clear belief: when farmers grow, the
              entire rural ecosystem grows with them. Our work is not only about selling
              feed or edible oil products. It is about creating trust, improving quality,
              supporting dealers, and helping livestock owners make better decisions every day.
            </p>

            <p>
              From animal nutrition to product quality, every step is guided by practical
              field understanding and long-term commitment. We believe that strong farmer
              relationships, honest service, and consistent product performance are the real
              foundation of sustainable growth.
            </p>

            <div className="zfast-values">
              <div>
                <strong>Farmers First</strong>
                <span>Kisan khush, toh hum khush.</span>
              </div>
              <div>
                <strong>Quality Conscious</strong>
                <span>Awareness, alignment, and attention in every product.</span>
              </div>
              <div>
                <strong>Happy Place</strong>
                <span>Personal and professional growth matters.</span>
              </div>
            </div>

            <div className="zfast-signoff">
              <strong>A Legacy in the Making</strong>
              <p>
                The founder’s journey stands as an example of purpose-led entrepreneurship —
                building not just a business, but a legacy of trust, quality, and service for
                India’s farming community.
              </p>
            </div>
          </div>

        </div>
        <style>{`
          .zfast-words-section {
            background: #ffffff;
            padding: 80px 20px;
            font-family: inherit;
            color: #183c29;
          }

          .zfast-words-wrap {
            max-width: 1180px;
            margin: 0 auto;
            display: grid;
            grid-template-columns: 0.9fr 1.1fr;
            gap: 56px;
            align-items: center;
          }

          .zfast-words-image {
            position: relative;
            border-radius: 28px;
            overflow: hidden;
            background: #eef8f1;
            box-shadow: 0 18px 45px rgba(15, 107, 58, 0.14);
          }

          .zfast-words-image img {
            width: 100%;
            height: 560px;
            object-fit: cover;
            object-position: center 18%;
            display: block;
          }

          .zfast-founder-caption {
            position: absolute;
            inset: auto 0 0;
            z-index: 2;
            margin: 0;
            padding: 40px 24px 20px;
            background: linear-gradient(180deg, transparent, rgba(10, 42, 25, 0.88));
            color: #ffffff;
          }

          .zfast-founder-name {
            margin: 0;
            font-size: clamp(18px, 2vw, 24px);
            font-weight: 900;
            letter-spacing: 0.01em;
            line-height: 1.2;
          }
          
          .zfast-founder-title {
            margin: 4px 0 0;
            font-size: clamp(15px, 1.6vw, 18px);
            font-weight: 700;
            color: rgba(255,255,255,0.9);
          }

          .zfast-eyebrow {
            display: inline-block;
            margin-bottom: 14px;
            padding: 7px 14px;
            border-radius: 999px;
            background: #eaf7ef;
            color: #0f6b3a;
            font-size: 12px;
            font-weight: 800;
            letter-spacing: 1px;
            text-transform: uppercase;
          }

          .zfast-words-content h2 {
            margin: 0 0 18px;
            color: #0e3b24;
            font-size: clamp(30px, 4vw, 52px);
            line-height: 1.05;
            font-weight: 800;
          }

          .zfast-quote {
            margin: 0 0 22px;
            padding-left: 22px;
            border-left: 5px solid #0f6b3a;
            color: #123d28;
            font-size: 18px;
            line-height: 1.65;
            font-weight: 500;
          }

          .zfast-words-content p {
            margin: 0 0 16px;
            color: #42564b;
            font-size: 15.5px;
            line-height: 1.75;
            font-weight: 400;
          }

          .zfast-values {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 14px;
            margin: 28px 0;
          }

          .zfast-values div {
            padding: 18px 16px;
            border-radius: 18px;
            background: #f5fff8;
            border: 1px solid #d8eddd;
          }

          .zfast-values strong {
            display: block;
            margin-bottom: 8px;
            color: #0f6b3a;
            font-size: 14px;
            font-weight: 800;
          }

          .zfast-values span {
            display: block;
            color: #5d6f65;
            font-size: 13.5px;
            line-height: 1.55;
          }

          .zfast-signoff {
            margin-top: 22px;
            padding: 22px 24px;
            border-radius: 22px;
            background: linear-gradient(135deg, #0f6b3a, #154b31);
            color: #ffffff;
          }

          .zfast-signoff strong {
            display: block;
            margin-bottom: 8px;
            font-size: 18px;
          }

          .zfast-signoff p {
            margin: 0;
            color: rgba(255,255,255,0.88);
            font-size: 14.5px;
            line-height: 1.7;
          }

          @media (max-width: 900px) {
            .zfast-words-section {
              padding: 56px 16px;
            }

            .zfast-words-wrap {
              grid-template-columns: 1fr;
              gap: 32px;
            }

            .zfast-words-image img {
              height: 420px;
              object-position: center 14%;
            }

            .zfast-values {
              grid-template-columns: 1fr;
            }

            .zfast-quote {
              font-size: 16px;
            }

            .zfast-words-content p {
              font-size: 15px;
            }
          }

          @media (max-width: 560px) {
            .zfast-words-section {
              padding: 40px 16px;
            }

            .zfast-words-wrap {
              gap: 24px;
            }

            .zfast-words-image img {
              height: auto;
              aspect-ratio: 4 / 5;
              object-fit: contain;
              object-position: center top;
            }

            .zfast-founder-caption {
              padding: 32px 18px 16px;
            }
            .zfast-founder-name {
              font-size: 18px;
            }

            .zfast-quote {
              font-size: 15px;
              line-height: 1.5;
            }

            .zfast-words-content p {
              font-size: 14.5px;
              line-height: 1.6;
            }

            .zfast-signoff {
              padding: 18px;
            }

            .zfast-signoff strong {
              font-size: 16px;
            }

            .zfast-signoff p {
              font-size: 13.5px;
            }
          }
        `}</style>
      </section>

      <PurposeAccordion />
    </main>
  );
}
